import React, { Component } from 'react';

import { ClickableComponent } from '@schoolinks/ui-components';
import cn from 'classnames';
import { noop } from 'lodash';
import { shape, bool, func, string, number } from 'prop-types';
import { connect } from 'react-redux';
import { withState } from 'recompose';
import { bindActionCreators } from 'redux';

import { faCircleUser } from '@fortawesome/pro-regular-svg-icons/faCircleUser';
import { faCircleXmark } from '@fortawesome/pro-regular-svg-icons/faCircleXmark';
import { faXmark } from '@fortawesome/pro-regular-svg-icons/faXmark';

import removeJwt from 'actions/removeJwt';
import Button from 'components/Common/Buttons/ButtonV3';
import FontAwesomeIcon from 'components/Common/FontAwesomeIcon';
import { isElementaryPage } from 'components/Common/LayoutV2/utils';
import LoadingOverlay from 'components/Common/LoadingOverlay';
import Tooltip from 'components/Common/Tooltip';
import * as modalActions from 'components/Modals/actions';
import withRouter from 'containers/withRouter';

import withUserType from 'containers/withUserType';
import { RouterPropTypes } from 'propTypes/RouterPropTypes';

import * as actions from 'modules/StudentDemoAccount/actions';

import s from './StudentDemoAccount.css';

@withUserType
@withState('modalOpen', 'setModalOpen', false)
@withState('stage', 'setStage', undefined)
@withState('busy', 'setBusy', false)
@withRouter
@connect(
  ({ studentDemoAccount, user }) => ({
    ...studentDemoAccount,
    studentPreviewCounselor: user.studentPreviewCounselor,
    userId: user.userId,
    currentUserPicture: user.profilePic,
  }),
  dispatch => bindActionCreators({ ...actions, ...modalActions }, dispatch),
)
class StudentDemoAccount extends Component {
  componentDidUpdate(prevProps) {
    const {
      modalOpen,
      setStage,
      stage,
      user: { isCounselor, isDemoStudent },
      userId,
      showModal,
      router: {
        location: { pathname },
      },
    } = this.props;
    const { stage: prevStage } = prevProps;
    if (stage && stage !== prevStage) {
      const isElementary = isElementaryPage(pathname);

      const modalProps = {
        open: modalOpen,
        onClose: () => this.onClose(),
        stage,
        isElementary,
        setStage,
        isCounselor,
        currentStudentUserPk: isDemoStudent ? userId : undefined,
      };
      showModal('STUDENT_DEMO_ACCOUNT', modalProps);
    }
  }

  onClose = () => {
    const {
      setModalOpen,
      user: { isDemoStudent },
      userId,
      resetStudentInfo,
      setStage,
    } = this.props;
    setStage();
    if (isDemoStudent) {
      resetStudentInfo(isDemoStudent, userId);
    }
    setModalOpen(false);
  };

  onExitDemo = async () => {
    const {
      switchToCounselorAccount,
      setBusy,
      router: {
        location: { pathname },
      },
    } = this.props;
    setBusy(true);
    await switchToCounselorAccount();

    const isElementary = isElementaryPage(pathname);
    let redirectUrl = '/';

    if (isElementary) {
      redirectUrl = '/el/dashboard';
    }

    removeJwt(false);

    // Clear local storage after exiting student demo
    localStorage.removeItem('studentPreviewCounselor');

    window.location = redirectUrl;
  };

  onDemoClick = async () => {
    const {
      setStage,
      user: { isCounselor },
      userId,
      updateStudentInfo,
    } = this.props;
    if (isCounselor === true) {
      setStage('select');
    } else {
      updateStudentInfo({ field: 'pk', value: userId });
      setStage('info');
    }
  };

  render() {
    const {
      setStage,
      disabled,
      user: { isCounselor, isDemoStudent },
      userId,
      updateStudentInfo,
      busy,
      isMobile,
      k12AdminTooltip,
    } = this.props;

    return isMobile ? (
      <>
        {busy && <LoadingOverlay />}
        {isDemoStudent && (
          <ClickableComponent onClick={this.onExitDemo} className={s.menuItem}>
            <FontAwesomeIcon icon={faCircleXmark} />
            Exit Demo
          </ClickableComponent>
        )}
        <ClickableComponent
          role="button"
          tabIndex={0}
          disabled={disabled}
          onClick={async () => {
            if (isCounselor === true) {
              setStage('select');
            } else {
              updateStudentInfo({ field: 'pk', value: userId });
              setStage('info');
            }
          }}
          className={cn(s.menuItem, {
            [s.disabled]: disabled,
            [s.borderBottom]: isDemoStudent,
          })}
        >
          <FontAwesomeIcon icon={faCircleUser} />
          <span className={s.buttonLabel}>
            {isCounselor ? 'Demo as Student' : 'Switch User'}
          </span>
        </ClickableComponent>
      </>
    ) : (
      <>
        {busy && <LoadingOverlay />}
        {isDemoStudent && (
          <Button
            icon={<FontAwesomeIcon icon={faXmark} />}
            hasIconRight
            label="Exit Demo"
            variant="filled"
            onClick={this.onExitDemo}
            className={cn(s.button, s.closeButton)}
          />
        )}
        <Tooltip placement="bottom" title={isCounselor ? k12AdminTooltip : ''}>
          <div>
            <Button
              disabled={disabled}
              className={cn(s.button, disabled && s.disabled)}
              label={isCounselor ? 'Demo as student' : 'Switch user'}
              hasIconLeft
              icon={<FontAwesomeIcon icon={faCircleUser} />}
              onClick={this.onDemoClick}
              variant="outlined"
            />
          </div>
        </Tooltip>
      </>
    );
  }
}

StudentDemoAccount.propTypes = {
  user: shape({
    isCounselor: bool,
    isStudent: bool,
    isDemoStudent: bool,
  }),
  modalOpen: bool,
  setModalOpen: func,
  stage: string,
  setStage: func,
  updateStudentInfo: func,
  switchToCounselorAccount: func,
  resetStudentInfo: func,
  userId: number,
  showModal: func,
  disabled: bool,
  busy: bool,
  setBusy: func,
  router: RouterPropTypes,
  isMobile: bool,
  k12AdminTooltip: string,
};

StudentDemoAccount.defaultProps = {
  user: {},
  modalOpen: false,
  setModalOpen: noop,
  stage: null,
  setStage: noop,
  updateStudentInfo: noop,
  switchToCounselorAccount: noop,
  resetStudentInfo: noop,
  userId: null,
  showModal: noop,
  disabled: false,
  busy: false,
  setBusy: noop,
  router: {},
  isMobile: false,
  k12AdminTooltip: null,
};

export default StudentDemoAccount;
